import { useReactiveVar } from '@apollo/client'
import {
  Button,
  Typography,
  Box,
  Menu,
  MenuItem,
  Divider,
  ListSubheader,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Plus } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { projectVar } from '../../apollo/reactive-vars'
import {
  CreatePricingModelInput,
  PricingModelFragment,
  useGlobalPricingModelsQuery,
} from '../../generated/graphql'
import { usePricingModels } from '../../pricing-model/use-pricing-models'
import {
  mapFormValuesToInput,
  mapPricingModelToValues,
  PricingModelDialog,
  PricingModelFormValues,
} from '../pricing-models/PricingModelDialog'
import { PricingModelAccordion } from '../pricing-models/PricingModelAccordion'
import { useSidebarStyles } from '../pricing-models/use-sidebar-styles'

export function CostsSidebarContent(): React.ReactElement {
  const classes = useSidebarStyles()
  const { t } = useTranslation()
  const [selectedBasePricingModel, setSelectedBasePricingModel] =
    useState<PricingModelFragment | null>(null)
  const [globalPricingModels, setGlobalPricingModels] = useState<
    PricingModelFragment[]
  >([])
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const project = useReactiveVar(projectVar)
  const { createPricingModel } = usePricingModels()
  const { data } = useGlobalPricingModelsQuery()

  useEffect(() => {
    const models =
      data && data?.globalPricingModels ? data.globalPricingModels : []
    const sortedModels = [...models].sort((a, b) =>
      a.name.localeCompare(b.name),
    )
    setGlobalPricingModels(sortedModels)
  }, [data])

  const onCreate = async (values: PricingModelFormValues) => {
    if (!project) {
      throw new Error(
        'Project missing when trying to create project-specific pricing model',
      )
    }

    const input = mapFormValuesToInput<CreatePricingModelInput>(values)
    await createPricingModel(project, input)

    setSelectedBasePricingModel(null)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (pricingModel: PricingModelFragment) => {
    setSelectedBasePricingModel(pricingModel)
    setAnchorEl(null)
  }

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <Box p={4} display="flex" justifyContent="center">
        <Button
          id="add-pricing-model"
          variant="contained"
          onClick={handleMenuClick}
          fullWidth
          aria-controls="pricing-model-menu"
          aria-haspopup="true"
        >
          <Box className={classes.buttonContent}>
            <Plus />
            <Typography variant="body1">{t('pricingModels.create')}</Typography>
          </Box>
        </Button>
        <Menu
          id="pricing-model-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <ListSubheader>{t('pricingModels.chooseBase')}</ListSubheader>
          <Divider />
          {globalPricingModels.map((pricingModel) => (
            <MenuItem
              key={pricingModel.id}
              data-cy="global-pricing-model-menu-item"
              onClick={() => handleMenuItemClick(pricingModel)}
            >
              {pricingModel.name}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {project?.pricingModels.map((pricingModel) => (
        <PricingModelAccordion
          key={`pricing-model-${pricingModel.id}`}
          pricingModel={pricingModel}
          project={project}
        />
      ))}

      <PricingModelDialog
        isOpen={Boolean(selectedBasePricingModel)}
        isCreate
        defaultValues={mapPricingModelToValues(selectedBasePricingModel)}
        onSubmit={onCreate}
        onClose={() => setSelectedBasePricingModel(null)}
      />
    </Box>
  )
}
